import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, FileText } from "lucide-react";
import SEOHead from "../components/layout/SEOHead";
import { CartLines } from "../components/cart/Cart";
import { CommerceGate } from "../components/cart/CommerceGate";
import { useCart, fmt } from "../context/CartContext";

export const quoteHandoff = (lines, extra = {}) => {
  sessionStorage.setItem("hwl_quote_prefill", JSON.stringify({ lines: lines.map((l) => ({ sku: l.sku, name: l.name, model: l.model, quantity: l.quantity })), ...extra }));
};

const CartPage = () => (
  <CommerceGate title="Cart"><CartContent /></CommerceGate>
);

const CartContent = () => {
  const { lines, subtotal, count } = useCart();
  const [params] = useSearchParams();
  const cancelled = params.get("cancelled") === "1";

  return (
    <>
      <SEOHead title="Your Cart | Highwall Lift" description="Review your Highwall Lift order." canonicalPath="/cart" />
      <div className="min-h-screen bg-[#0A0A0A] pt-28 pb-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-xs font-bold mb-3">Highwall Lift</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight uppercase text-white mb-8" data-testid="cart-page-title">Your Cart</h1>
          {cancelled && <p className="mb-6 p-4 border border-white/10 bg-[#141414] text-gray-300" data-testid="cart-cancelled-notice">Checkout was cancelled. Your cart has been saved — no payment was made.</p>}

          {lines.length === 0 ? (
            <div className="bg-[#141414] border border-white/10 p-10 text-center" data-testid="cart-page-empty">
              <p className="text-gray-400 mb-6">Your cart is empty.</p>
              <Link to="/#products" className="inline-flex items-center gap-2 px-6 py-3 bg-signal-red text-white font-bold uppercase text-sm tracking-wide" data-testid="cart-page-shop">Browse Products <ArrowRight className="w-4 h-4" /></Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 bg-[#141414] border border-white/10 px-6">
                <CartLines />
              </div>
              <aside className="bg-[#141414] border border-white/10 p-6 h-fit">
                <div className="flex justify-between items-end mb-2">
                  <span className="text-gray-400">Subtotal ({count} set{count === 1 ? "" : "s"})</span>
                  <span className="font-heading text-2xl font-black text-white" data-testid="cart-page-subtotal">{fmt(subtotal)}</span>
                </div>
                <p className="text-gray-500 text-xs mb-6">Shipping (FedEx) and applicable tax are calculated after you enter your shipping address. U.S. online checkout; international orders by quote.</p>
                <Link to="/checkout" className="block text-center w-full px-6 py-4 bg-signal-red text-white font-bold uppercase tracking-wide hover:bg-signal-red/90 transition-colors mb-3" data-testid="cart-checkout-button">Proceed to Checkout</Link>
                <Link to="/#products" className="block text-center w-full px-6 py-3 text-gray-300 hover:text-white text-sm uppercase tracking-wide" data-testid="cart-continue-shopping">Continue Shopping</Link>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-gray-400 text-sm mb-3">Large order, dealer or rental fleet, PO / net terms, tax-exempt, or outside the U.S.?</p>
                  <Link to="/#quote" onClick={() => quoteHandoff(lines, { reason: "cart" })} className="inline-flex items-center gap-2 text-signal-red font-bold uppercase text-sm hover:text-white transition-colors" data-testid="cart-request-quote">
                    <FileText className="w-4 h-4" /> Request a Quote with this cart
                  </Link>
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartPage;
